import { c as _c } from "react/compiler-runtime";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { RelayKitProviderWrapper, TooltipProvider } from "../../../../global-account/react/index.js";
import { createWagmiConfig } from "../../../../global-account/react/utils/createWagmiConfig.js";
import { loadGA4Script } from "../../../../global-account/utils/analytics.js";
import { WalletProvider } from "../../../../wallet/react/index.js";
import "@relayprotocol/relay-kit-ui/styles.css";
import { useEffect, useMemo } from "react";
import { resetClients, setClientType } from "../../../client-manager.js";
import { setB3ApiUrl } from "../../../../app.shared.js";
import { setApiUrl as setNotificationsApiUrl } from "../../../../notifications/services/api.js";
import { resetBetterAuthClient } from "../../../better-auth-client.js";
import { StyleRoot } from "../StyleRoot.js";
import { setToastContext, ToastProvider, useToastContext } from "../Toast/index.js";
import AuthenticationProvider from "./AuthenticationProvider.js";
import { B3ConfigProvider } from "./B3ConfigProvider.js";
import BetterAuthProvider from "./BetterAuthProvider.js";
import { LocalSDKProvider } from "./LocalSDKProvider.js";
const NOTIFICATIONS_URL_BY_ENV = {
  development: "https://dev-notifications.b3.fun",
  production: "https://notifications.b3.fun"
};
/**
 * Main B3Provider component
 */
export function B3Provider({
  theme = "light",
  children,
  accountOverride,
  environment,
  automaticallySetFirstEoa,
  defaultEoaProvider,
  simDuneApiKey,
  // deprecated since v0.0.87
  toaster: _toaster,
  clientType = "rest",
  rpcUrls,
  partnerId,
  stripePublishableKey,
  onConnect,
  onLogout,
  connectors,
  overrideDefaultConnectors = false,
  createClientReferenceId,
  defaultPermissions,
  disableBSMNTAuthentication = false,
  queryClient,
  authStrategy = "thirdweb",
  apiUrl,
  notificationsApiUrl
}) {
  // Override the B3 API URL when the `apiUrl` prop is provided.
  // Must run before client initialization and auth providers mount.
  useEffect(() => {
    setB3ApiUrl(apiUrl ?? null);
    if (apiUrl) {
      resetBetterAuthClient();
      resetClients();
    }
    return () => {
      setB3ApiUrl(null);
    };
  }, [apiUrl]);
  useEffect(() => {
    setNotificationsApiUrl(notificationsApiUrl ?? NOTIFICATIONS_URL_BY_ENV[environment ?? "production"]);
  }, [notificationsApiUrl, environment]);
  // Initialize Google Analytics on mount
  useEffect(() => {
    loadGA4Script();
  }, []);
  // Set the client type when provider mounts
  useEffect(() => {
    setClientType(clientType);
  }, [clientType]);
  const wagmiConfig = useMemo(() => createWagmiConfig({
    partnerId,
    rpcUrls,
    connectors,
    overrideDefaultConnectors
  }), [partnerId, rpcUrls, connectors, overrideDefaultConnectors]);
  return _jsx(WalletProvider, {
    wagmiConfig: wagmiConfig,
    queryClient: queryClient,
    children: _jsx(TooltipProvider, {
      children: _jsx(ToastProvider, {
        children: _jsx(LocalSDKProvider, {
          onConnectCallback: onConnect,
          onLogoutCallback: onLogout,
          disableBSMNTAuthentication: disableBSMNTAuthentication,
          children: _jsxs(B3ConfigProvider, {
            accountOverride: accountOverride,
            environment: environment,
            automaticallySetFirstEoa: !!automaticallySetFirstEoa,
            theme: theme,
            clientType: clientType,
            partnerId: partnerId,
            stripePublishableKey: stripePublishableKey,
            createClientReferenceId: createClientReferenceId,
            defaultPermissions: defaultPermissions,
            authStrategy: authStrategy,
            children: [_jsx(ToastContextConnector, {}), _jsxs(RelayKitProviderWrapper, {
              simDuneApiKey: simDuneApiKey,
              children: [children, _jsx(StyleRoot, {
                id: "b3-root"
              })]
            }), authStrategy === "better-auth" ? _jsx(BetterAuthProvider, {
              partnerId: partnerId
            }) : _jsx(AuthenticationProvider, {
              partnerId: partnerId,
              automaticallySetFirstEoa: !!automaticallySetFirstEoa,
              defaultEoaProvider: defaultEoaProvider
            })]
          })
        })
      })
    })
  });
}
/**
 * Component to connect the toast context to the global toast API
 */
function ToastContextConnector() {
  const $ = _c(6);
  const toastContext = useToastContext();
  let t0;
  if ($[0] !== toastContext.addToast || $[1] !== toastContext.clearAll || $[2] !== toastContext.removeToast) {
    t0 = () => {
      setToastContext({
        addToast: toastContext.addToast,
        removeToast: toastContext.removeToast,
        clearAll: toastContext.clearAll
      });
    };
    $[0] = toastContext.addToast;
    $[1] = toastContext.clearAll;
    $[2] = toastContext.removeToast;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== toastContext) {
    t1 = [toastContext];
    $[4] = toastContext;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  useEffect(t0, t1);
  return null;
}